import React, { ChangeEvent, DragEvent, FC, memo, ReactElement, ReactNode, useCallback, useId, useRef, useState } from 'react';
import i18next from 'i18next';
import { FileEarmarkText, X } from 'react-bootstrap-icons';

type DropZoneProps = {
  label: string;
  accept: string;
  previewUrl: string | null;
  onFileSelect: (file: File) => void;
  onClear?: () => void;
  disabled?: boolean;
  multiple?: boolean;
  maxSizeMB?: number;
  onError?: (msg: string) => void;
}

type FileKind = 'video' | 'audio' | 'image' | 'file';

function parseAccept(accept: string): string[] {
  return accept.split(',').map((acc: string) => acc.trim()).filter(Boolean);
}

function matchesAccept(file: File, accept: string): boolean {
  const parts: string[] = parseAccept(accept);

  if (parts.length === 0) {
    return true;
  }

  return parts.some((acc: string) => {
    if (acc.endsWith('/*')) {
      const prefix: string = acc.slice(0, acc.indexOf('/*'));
      return file.type.startsWith(prefix + '/');
    }
    return file.type === acc;
  });
}

function kindFromAccept(accept: string): FileKind {
  const list: string[] = parseAccept(accept);

  if (list.some((acc: string) => acc.startsWith('video/') || acc === 'video/*')) {
    return 'video';
  }

  if (list.some((acc: string) => acc.startsWith('audio/') || acc === 'audio/*')) {
    return 'audio';
  }

  if (list.some((acc: string) => acc.startsWith('image/') || acc === 'image/*')) {
    return 'image';
  }

  return 'file';
}

const DropZone: FC<DropZoneProps> = ({
  label,
  accept,
  previewUrl,
  onFileSelect,
  onClear,
  disabled = false,
  multiple = false,
  maxSizeMB,
  onError }: DropZoneProps): ReactElement => {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputId: string = useId();

  const inputRef = useRef<HTMLInputElement | null>(null);

  const openPicker = useCallback(() => inputRef.current?.click(), []);

  const pickFirstValidFile = (fileList: FileList | null): File | null => {
    if (!fileList || fileList.length === 0) {
      return null;
    }

    const items: File[] = Array.from(fileList);
    const first: File | null = items.find((file: File) => matchesAccept(file, accept)) ?? null;
    return first ?? items[0] ?? null;
  };

  const validateAndSelect = (file: File | null): void => {
    if (!file) {
      return;
    }

    if (!matchesAccept(file, accept)) {
      onError?.(i18next.t('generic.invalidFileType'));
      return;
    }

    if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
      onError?.(i18next.t('generic.fileTooLarge'));
      return;
    }

    onFileSelect(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    const file: File | null = pickFirstValidFile(event.dataTransfer?.files ?? null);

    validateAndSelect(file);
    setIsDragOver(false);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();

    setIsDragOver(false);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file: File | null = pickFirstValidFile(event.target.files);
    validateAndSelect(file);
  };

  const handleClear = (): void => {
    onClear?.();
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const kind: FileKind = kindFromAccept(accept);

  let previewContent: ReactNode | null = null;

  if (previewUrl) {
    if (kind === 'video' || kind === 'image') {
      previewContent = (
        <div
          className="mt-2 rounded-3 position-relative overflow-hidden bg-white w-100"
          style={{ height: 220 }}
        >
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
            {kind === 'video' ? (
              <video
                src={previewUrl}
                controls
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <img
                src={previewUrl}
                alt={i18next.t('generic.preview')}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            )}
          </div>

          {onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center border position-absolute"
              style={{ top: 8, right: 8, width: 28, height: 28, color: '#ff695f', backgroundColor: 'rgba(255, 255, 255, 0.92)', zIndex: 2 }}
            >
              <X size={20} className="flex-shrink-0"/>
            </button>
          )}
        </div>
      );
    } else if (kind === 'audio') {
      previewContent = (
        <div
          className="mt-2 rounded-3 bg-white d-flex align-items-center gap-2"
          style={{ padding: '0.5rem 0.75rem' }}
        >
          <audio src={previewUrl} controls style={{ width: '100%', height: 40 }}/>

          {onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center border"
              style={{ width: 28, height: 28, color: '#ff695f', backgroundColor: 'rgba(255, 255, 255, 0.92)' }}
            >
              <X size={20} className="flex-shrink-0"/>
            </button>
          )}
        </div>
      );
    } else {
      previewContent = (
        <div
          className="mt-2 rounded-3 bg-white d-flex align-items-center position-relative"
          style={{ padding: '0.75rem 0.9rem', gap: '0.75rem' }}
        >
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 30, height: 30, backgroundColor: '#ffe3e0' }}
          >
            <FileEarmarkText size={20} />
          </div>

          <div className="d-flex flex-column" style={{ minWidth: 0, gap: '0.1rem' }}>
            <span className="fw-semibold text-truncate" style={{ fontSize: '0.9rem', color: '#333' }}>
              {i18next.t('generic.fileSelected')}
            </span>
            <span style={{ fontSize: '0.8rem', color: '#777' }}>
              {i18next.t('generic.openFile')}
            </span>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: '0.8rem' }}
            >
              {i18next.t('generic.openInNewTab')}
            </a>
          </div>

          {onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-light btn-sm rounded-circle d-inline-flex align-items-center justify-content-center border position-absolute"
              style={{ top: 8, right: 8, width: 28, height: 28, color: '#ff695f', backgroundColor: 'rgba(255, 255, 255, 0.92)' }}
            >
              <X size={20} className="flex-shrink-0"/>
            </button>
          )}
        </div>
      );
    }
  }

  return (
    <div
      role="button"
      aria-label={label}
      tabIndex={0}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openPicker();
        }
      }}
      className="border rounded-3 p-3 position-relative"
      style={{
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: isDragOver ? '#ff695f' : '#ccc',
        background: isDragOver ? '#fdecea' : '#f9f9f9',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        cursor: 'pointer',
        userSelect: 'none',
        outline: 'none',
        transition: 'border-color 0.15s ease, background 0.15s ease',
      }}
    >
      <p className="fw-bold text-center mb-2">{label}</p>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        id={inputId}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={handleChange}
        disabled={disabled}
      />

      <div className="d-flex justify-content-center align-items-center gap-2">
        <button
          type="button"
          onClick={openPicker}
          className="btn btn-outline-danger btn-sm mt-1"
          style={{
            borderColor: '#ff695f',
            color: '#ff695f',
          }}
        >
          {i18next.t('generic.chooseFile')}
        </button>
      </div>

      {previewContent}
    </div>
  );
};

export default memo(DropZone);
